import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, MapPin, Phone } from "lucide-react";
import EditableNote from "@/components/EditableNote";
import { COL_ESTADOS } from "@/components/ColocacionBadge";
import { Productos } from "@/components/empaque/EmpaqueCard";

export const EstadoPill = ({ k, big }) => {
  const e = COL_ESTADOS[k] || COL_ESTADOS.pendiente_empacar;
  return <span className={`${big ? "text-sm px-2 py-1" : "text-xs px-2 py-0.5"} font-black rounded uppercase ${e.c}`}>{e.l}</span>;
};

export const Paquetes = ({ paquetes }) => (
  <div className="space-y-1" data-testid="col-paquetes">
    {paquetes.length === 0 && <div className="text-sm text-zinc-500">Sin guías todavía (pendiente de empacar).</div>}
    {paquetes.map((p) => (
      <div key={p.guia} className="flex items-center justify-between gap-2" data-testid={`col-paquete-${p.guia}`}>
        <span className="font-mono text-base font-bold bg-zinc-900 text-white rounded px-2 py-0.5">{p.guia}</span>
        <span className="flex-1 text-xs text-zinc-500 truncate">{p.agencia}</span>
        <EstadoPill k={p.estado} />
        {p.seg_lbl && <span className="text-xs px-2 py-0.5 font-black rounded uppercase bg-[#002FA7] text-white" data-testid={`col-paquete-entrega-${p.guia}`}>{p.seg_lbl}</span>}
      </div>
    ))}
  </div>
);

export const ColocacionCard = ({ p, onOpen }) => (
  <button onClick={onOpen} className={`w-full text-left bg-white border-2 ${p.prioridad ? "border-red-600" : "border-zinc-200"} rounded-lg p-4 shadow-sm space-y-2`} data-testid={`col-card-${p.numero}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-2xl font-black tracking-tight">{p.numero}</span>
      <div className="flex gap-1">{p.anterior && <span className="bg-zinc-200 text-zinc-800 text-xs font-black px-2 py-1 rounded uppercase" title="Pedido de antes del último cierre mensual: en Hoja de Pedidos está en el Historial" data-testid={`col-anterior-${p.numero}`}>Período anterior</span>}{p.prioridad && <span className="bg-red-600 text-white text-xs font-black px-2 py-1 rounded uppercase">Prioridad</span>}<span className={`text-sm font-black px-2 py-1 rounded uppercase ${p.empacado ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`} data-testid={`col-empaque-${p.numero}`}>{p.empacado ? "Empacado" : "No empacado"}</span><EstadoPill k={p.estado} big /></div>
    </div>
    <div className="text-xl font-bold">{p.cliente}</div>
    {p.destino && <div className="text-sm text-zinc-500 flex gap-1"><MapPin className="w-4 h-4 shrink-0" />{p.destino}</div>}
    <Productos productos={p.productos} />
    <div className="text-sm font-bold">{p.paquetes.length} paquete(s)</div>
    {!p.empacado && <div className="text-sm font-bold text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid={`col-sin-empacar-${p.numero}`}>Sin empacar: traslado bloqueado hasta confirmar el empaque.</div>}
    <Paquetes paquetes={p.paquetes} />
    {p.notas_ent && <div className="text-sm bg-amber-50 border border-amber-200 rounded p-2 whitespace-pre-wrap">{p.notas_ent}</div>}
    {p.fotos.length > 0 && <div className="flex gap-1 overflow-x-auto">{p.fotos.slice(0, 4).map((f) => <img key={f} src={f} alt="empaque" className="w-16 h-16 object-cover rounded border" />)}</div>}
  </button>
);

const SubirFotos = ({ p, onDone }) => {
  const [busy, setBusy] = useState(0);
  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    setBusy(files.length);
    for (const f of files) {
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      try { await api.post(`/colocacion/${p.id}/fotos`, { foto_base64: b64 }); } catch (err) { toast.error(formatApiError(err)); }
      setBusy((n) => n - 1);
    }
    toast.success("Fotos guardadas");
    onDone();
  };
  return (
    <label className="w-full h-14 rounded-lg border-2 border-black text-lg font-black uppercase flex items-center justify-center gap-2 cursor-pointer" data-testid="col-fotos-add">
      <Camera className="w-6 h-6" /> {busy ? `Subiendo ${busy}…` : "Agregar fotos"}
      <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onFiles} disabled={!!busy} />
    </label>
  );
};

const Galeria = ({ titulo, fotos, testid }) => (
  <div className="rounded-lg border-2 border-zinc-200 p-4" data-testid={testid}>
    <div className="text-xs uppercase font-black text-zinc-500 mb-2">{titulo} ({fotos.length})</div>
    {fotos.length === 0 && <div className="text-sm text-zinc-400">Sin fotos.</div>}
    <div className="grid grid-cols-3 gap-2">{fotos.map((f) => <a key={f.url} href={f.url} target="_blank" rel="noreferrer"><img src={f.url} alt="foto" className="w-full aspect-square object-cover rounded border" /><div className="text-[10px] text-zinc-500">{f.by}</div></a>)}</div>
  </div>
);

export const ColocacionDetalle = ({ p, onReload }) => (
  <div className="space-y-4" data-testid="col-detalle">
    <div className={`rounded-lg border-2 p-4 ${p.prioridad ? "border-red-600" : "border-zinc-200"}`}>
      <div className="flex justify-between items-center"><div className="text-3xl font-black">{p.numero}</div><EstadoPill k={p.estado} big /></div>
      <div className="text-2xl font-bold mt-1">{p.cliente}</div>
      {p.telefono && <div className="flex gap-2 items-center mt-2"><Phone className="w-5 h-5 text-emerald-700" /><b>{p.telefono}</b>{p.telefono2 && <b> / {p.telefono2}</b>}</div>}
      {(p.direccion || p.destino) && <div className="flex gap-2 mt-1"><MapPin className="w-5 h-5 shrink-0 text-zinc-500" />{[p.direccion, p.destino].filter(Boolean).join(" · ")}</div>}
    </div>
    <div className="rounded-lg border-2 border-zinc-200 p-4"><div className="text-xs uppercase font-black text-zinc-500 mb-2">Productos</div><Productos productos={p.productos} big /></div>
    <div className="rounded-lg border-2 border-zinc-200 p-4 space-y-3">
      <div className="text-xs uppercase font-black text-zinc-500">Paquetes ({p.paquetes.length})</div>
      {p.paquetes.map((q) => (
        <div key={q.guia} className="border-t border-zinc-100 pt-2">
          <div className="flex justify-between items-center"><span className="font-mono text-xl font-bold">{q.guia}</span><EstadoPill k={q.estado} /></div>
          {q.seg_lbl && <div className="text-sm font-black text-[#002FA7]" data-testid={`col-det-entrega-${q.guia}`}>Entrega: {q.seg_lbl}{q.seg_at ? ` · ${q.seg_at}` : ""}</div>}
          {q.eventos.map((e, i) => <div key={i} className={`text-sm ${e.estado === "revertido" ? "text-red-700" : "text-zinc-600"}`}>{COL_ESTADOS[e.estado]?.l}: {e.fecha} · {e.by}{e.agencia ? ` · ${e.agencia}` : ""}{e.motivo ? ` · ${COL_ESTADOS[e.de]?.l} → ${COL_ESTADOS[e.a]?.l} · Motivo: ${e.motivo}` : ""}</div>)}
        </div>
      ))}
    </div>
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
      <div className="text-xs uppercase font-black text-amber-800 mb-1">Notas de entrega</div>
      <EditableNote orderId={p.id} modulo="notas_ent" initial={p.notas_ent} onSaved={onReload} />
    </div>
    <SubirFotos p={p} onDone={onReload} />
    <Galeria titulo="Fotos de empaque" fotos={p.fotos_det} testid="col-fotos-empaque" />
    <Galeria titulo="Fotos de colocación" fotos={p.fotos_col} testid="col-fotos-colocacion" />
  </div>
);
